import React, { KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import {
  createFilter,
  type ActionMeta,
  type MultiValue,
  type SingleValue,
  type CSSObjectWithLabel,
} from 'react-select';
import ReactSelectCreatable from 'react-select/creatable';

import { defaultStyles, mergeSelectStyles } from '../../utils';
import { CustomMenuList } from './custom-menu-list';

import type { SelectRefType, SelectProps } from '../../types';
import { type AvatarOption, AvatarSelectOption } from '../avatar-select/avatar-select-option';

import styles from './avatar-creatable-select.module.scss';

type AvatarSelectProps = SelectProps<AvatarOption> & {
  onCreate?: (label: string) => void;
  width?: number;
  setHasOptions?: (hasOptions?: boolean) => void;
};

const filterConfig: Parameters<typeof createFilter>[0] = {
  ignoreCase: true,
  ignoreAccents: true,
  stringify: (option) => `${(option.data as AvatarOption).isNew} ${option.value} `,
  trim: true,
  matchFrom: 'any',
};

const createOption = (label: string) => ({
  avatarSrc: '',
  value: label,
  label: label,
  isNew: true,
});

export const AvatarCreatableSelect = React.forwardRef<
  SelectRefType<AvatarOption>,
  AvatarSelectProps
>(
  (
    {
      options,
      defaultValue = [],
      isClearable = false,
      styles,
      onCreate,
      onChange,
      isMulti,
      isLoading,
      isDisabled,
      label,
      required,
      errorMessage,
      width,
      onInputChange,
      setHasOptions,
      ...props
    },
    ref,
  ) => {
    const currentStyles = mergeSelectStyles(defaultStyles<AvatarOption>(), {
      ...styles,
      control: () => ({
        borderWidth: '0',
        borderStyle: 'none',
        borderColor: 'transparent',
        boxShadow: 'none',
        width: '100%',
      }),
      menuList: () => ({
        borderRadius: '0.4rem',
        background: 'var(--color-grayscale-tuna)',
      }),
      menu: (baseStyles: CSSObjectWithLabel) => ({
        ...baseStyles,
        borderRadius: '0.4rem',
        background: 'var(--color-grayscale-tuna)',
        boxShadow: '0px 4px 24px 0px rgba(0, 0, 0, 0.20)',
        width: `${width}px`,
      }),
      menuPortal: (baseStyles: CSSObjectWithLabel) => ({
        ...baseStyles,
        zIndex: 'calc(var(--layer-modal) + 1)',
        pointerEvents: 'auto',
      }),
      option: () => ({
        padding: 0,
      }),
      dropdownIndicator: () => ({
        display: 'none',
      }),
      clearIndicator: () => ({
        display: 'none',
      }),
    });

    const [inputValue, setInputValue] = useState<string>();
    const [selectedOption, setSelectedOption] = useState<
      MultiValue<AvatarOption> | SingleValue<AvatarOption>
    >();

    // Wheel listener added while the menu is open. Kept in a ref so it can be
    // removed on menu close / unmount instead of leaking on every open.
    const wheelHandlerRef = useRef<((event: WheelEvent) => void) | null>(null);
    const removeWheelListener = () => {
      if (wheelHandlerRef.current) {
        document.removeEventListener('wheel', wheelHandlerRef.current, { capture: true });
        wheelHandlerRef.current = null;
      }
    };
    useEffect(() => removeWheelListener, []);

    const handleKeyDown: KeyboardEventHandler = (event) => {
      if (['Enter', 'Tab'].includes(event.key)) {
        handleCreate();
      }
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
      event.preventDefault();

      handleCreate();
    };

    const handleChange = (
      option: MultiValue<AvatarOption> | SingleValue<AvatarOption>,
      actionMeta: ActionMeta<AvatarOption>,
    ) => {
      setSelectedOption(option);
      onChange?.(option, actionMeta);
    };

    const handleCreate = () => {
      if (!inputValue) return;

      onCreate?.(inputValue);

      const newOption = createOption(inputValue);
      setSelectedOption(newOption);
    };

    return (
      <div className={styles['container']}>
        {label && (
          <label className={styles['label']}>
            {label} {required && <b>*</b>}
          </label>
        )}

        <ReactSelectCreatable
          ref={ref}
          {...props}
          options={options}
          defaultValue={defaultValue}
          value={selectedOption}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onInputBlur={handleBlur}
          styles={currentStyles}
          isClearable={isClearable}
          isMulti={isMulti}
          isLoading={isLoading}
          isDisabled={isLoading || isDisabled}
          inputValue={inputValue}
          onInputChange={(value, actionMeta) => {
            if (!['input-blur', 'menu-close'].includes(actionMeta.action)) {
              setInputValue?.(value);
              onInputChange?.(value, actionMeta);
            }
          }}
          noOptionsMessage={() => {
            // Defer out of render (see custom-menu-list) to avoid updating the
            // parent component during react-select's render.
            queueMicrotask(() => setHasOptions?.(false));
            return null;
          }}
          getNewOptionData={(inputValue, optionLabel) => ({
            avatarSrc: '',
            value: inputValue,
            label: optionLabel,
            isNew: true,
          })}
          filterOption={(option, inputValue) => {
            if (option.data.isNew) return false;

            return createFilter(filterConfig)(option, inputValue);
          }}
          onMenuOpen={() => {
            removeWheelListener();
            const handler = (event: WheelEvent) => event.stopPropagation();
            wheelHandlerRef.current = handler;
            document.addEventListener('wheel', handler, { capture: true });
          }}
          onMenuClose={removeWheelListener}
          components={{
            MenuList: CustomMenuList,
            Option: AvatarSelectOption,
          }}
          {...(onCreate ? { onCreateOption: handleCreate } : undefined)}
          blurInputOnSelect
          tabSelectsValue
          // @ts-ignore
          setHasOptions={setHasOptions}
        />

        {errorMessage && <span className={styles['error']}>{errorMessage}</span>}
      </div>
    );
  },
);

AvatarCreatableSelect.displayName = 'AvatarCreatableSelect';
